import React, { useState, useEffect } from 'react';
import { getApiEndpoint } from '../../../../config/api';
import './AnchorPoints.css';
import { toast } from 'react-toastify';
import CreateAnchorPointModal from './CreateAnchorPointModal';
import EditAnchorPointModal from './EditAnchorPointModal';
import AssetDetailModal from '../shared/AssetDetailModal';
import { openLatestTestReport } from '../shared/openLatestTestReport';
import { isSuperAdmin } from '../../../../utils/roleUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEdit, faTrash, faSearch, faFilter, faAnchor, faPlus, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../../../config/api';

const STATUS_BADGE_CLASS = {
  pass: 'rs-badge rs-badge-pass',
  passed: 'rs-badge rs-badge-passed',
  fail: 'rs-badge rs-badge-fail',
  failed: 'rs-badge rs-badge-failed',
  pending: 'rs-badge rs-badge-pending',
  overdue: 'rs-badge rs-badge-overdue',
  unknown: 'rs-badge rs-badge-unknown'
};

const getBadgeClass = (status) => {
  const key = (status || 'unknown').toLowerCase();
  return STATUS_BADGE_CLASS[key] || STATUS_BADGE_CLASS.unknown;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB');
  } catch {
    return 'N/A';
  }
};

export default function AnchorPoints({ propertyId }) {
  const [anchorPoints, setAnchorPoints] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAnchorPoint, setEditingAnchorPoint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [detailAsset, setDetailAsset] = useState(null);

  const fetchAnchorPoints = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/anchor-points`), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error('Failed to fetch anchor points');

      const data = await response.json();
      const formattedData = (Array.isArray(data) ? data : [data]).map(point => ({
        ...point,
        id: point.id || 'N/A',
        name: point.name || point.point_name || `Anchor Point ${point.id || 'N/A'}`,
        location: point.location_description || point.location || 'N/A',
        type: point.type || 'Fall Arrest',
        status: point.status || 'Unknown',
        installationDate: formatDate(point.installation_date),
        lastTestDate: formatDate(point.last_inspection_date),
        nextTestDate: formatDate(point.next_inspection_date),
        propertyId: point.property_id,
        notes: point.notes || ''
      }));
      setAnchorPoints(formattedData);
    } catch (err) {
      console.error('Error fetching anchor points:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = (newPoint) => {
    setAnchorPoints(prev => [...prev, {
      ...newPoint,
      name: newPoint.name || `Anchor Point ${newPoint.id}`,
      location: newPoint.location_description || 'N/A',
      lastTestDate: formatDate(newPoint.last_inspection_date),
      nextTestDate: formatDate(newPoint.next_inspection_date),
      installationDate: formatDate(newPoint.installation_date),
      propertyId: newPoint.property_id,
      status: newPoint.status || 'Unknown'
    }]);
  };

  const handleViewTestData = (point) => {
    openLatestTestReport({
      propertyId,
      assetType: 'anchor_point',
      assetId: point.id,
      fallbackAsset: point,
      emptyMessage: 'No test report available for this anchor point',
      onEmpty: (msg) => toast.info(msg),
    });
  };

  const handleDeleteAnchorPoint = async (pointId) => {
    if (!window.confirm('Are you sure you want to delete this anchor point?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/anchor-points/${pointId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to delete anchor point');
      setAnchorPoints(prev => prev.filter(p => p.id !== pointId));
      toast.success('Anchor point deleted successfully');
    } catch (err) {
      console.error('Error deleting anchor point:', err);
      toast.error('Failed to delete anchor point');
    }
  };

  useEffect(() => {
    if (propertyId) fetchAnchorPoints();
  }, [propertyId]);

  // Auto-open asset from drawing hotspot navigation
  useEffect(() => {
    const handler = (e) => {
      const { assetId, assetTable } = e.detail || {};
      if (assetTable === 'anchor_points' && assetId && anchorPoints.length) {
        const asset = anchorPoints.find(a => a.id === parseInt(assetId));
        if (asset) setDetailAsset(asset);
      }
    };
    window.addEventListener('openAssetFromHotspot', handler);
    return () => window.removeEventListener('openAssetFromHotspot', handler);
  }, [anchorPoints]);

  useEffect(() => {
    if (!showFilterDropdown) return;
    const handler = (e) => {
      if (!e.target.closest('.rs-filter-dropdown') && !e.target.closest('.filter-toggle-btn')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterDropdown]);

  const filtered = anchorPoints
    .filter(p => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return [p.name, p.location, p.type, p.status].some(v => v && v.toLowerCase().includes(q));
    })
    .filter(p => typeFilter === 'All' || p.type === typeFilter);

  if (error) {
    return (
      <div className="anchor-points-section">
        <h1>Anchor Points</h1>
        <div className="rs-error-alert">
          <FontAwesomeIcon icon={faAnchor} />
          <span>{error}</span>
          <button onClick={fetchAnchorPoints}>Retry</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="anchor-points-section">
        <h1>Anchor Points</h1>
        <div className="rs-skeleton">
          {[...Array(6)].map((_, i) => <div key={i} className="rs-skeleton-row" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="anchor-points-section">
      <h1>Anchor Points</h1>

      {/* Toolbar */}
      <div className="rs-toolbar">
        <div className="rs-search-bar">
          <FontAwesomeIcon icon={faSearch} className="rs-search-icon" />
          <input
            type="text"
            placeholder="Search anchor points..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table / Cards */}
      <div className="rs-table-card">
        {/* Desktop Table */}
        <div className="rs-table-scroll">
          <table className="rs-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th>Type</th>
                <th>Status</th>
                <th>Last Test</th>
                <th>Next Due</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="7">
                    <div className="rs-empty-state">
                      <div className="rs-empty-icon"><FontAwesomeIcon icon={faAnchor} /></div>
                      <h3 className="rs-empty-title">
                        {anchorPoints.length === 0 ? 'No anchor points yet' : 'No results found'}
                      </h3>
                      <p className="rs-empty-desc">
                        {anchorPoints.length === 0
                          ? 'Add your first anchor point to start tracking safety compliance.'
                          : 'Try adjusting your search or filter criteria.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map(point => (
                <tr key={point.id}>
                  <td title={point.name}>{point.name}</td>
                  <td title={point.location}>{point.location}</td>
                  <td>{point.type}</td>
                  <td>
                    <span className={getBadgeClass(point.status)}>
                      {point.status || 'Unknown'}
                    </span>
                  </td>
                  <td>{point.lastTestDate}</td>
                  <td>{point.nextTestDate}</td>
                  <td>
                    <div className="rs-actions">
                      <button className="rs-action-btn" onClick={() => handleViewTestData(point)} title="View Test Data" aria-label="View test data">
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button className="rs-action-btn" onClick={() => setDetailAsset(point)} title="Test History" aria-label="View test history">
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                      </button>
                      {isSuperAdmin() && (<>
                      <button className="rs-action-btn" onClick={() => { setEditingAnchorPoint(point); setShowEditModal(true); }} title="Edit" aria-label="Edit anchor point">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="rs-action-btn rs-action-delete" onClick={() => handleDeleteAnchorPoint(point.id)} title="Delete" aria-label="Delete anchor point">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                      </>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="rs-mobile-cards">
          {filtered.length === 0 ? (
            <div className="rs-empty-state">
              <div className="rs-empty-icon"><FontAwesomeIcon icon={faAnchor} /></div>
              <h3 className="rs-empty-title">
                {anchorPoints.length === 0 ? 'No anchor points yet' : 'No results found'}
              </h3>
              <p className="rs-empty-desc">
                {anchorPoints.length === 0
                  ? 'Add your first anchor point to start tracking safety compliance.'
                  : 'Try adjusting your search or filter criteria.'}
              </p>
            </div>
          ) : filtered.map(point => (
            <div key={point.id} className="rs-mobile-card">
              <div className="rs-mobile-card-header">
                <span className="rs-mobile-card-title">{point.name}</span>
                <span className={getBadgeClass(point.status)}>{point.status || 'Unknown'}</span>
              </div>
              <div className="rs-mobile-card-meta">
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Location</span>
                  <span className="rs-mobile-card-value">{point.location}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Type</span>
                  <span className="rs-mobile-card-value">{point.type}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Last Test</span>
                  <span className="rs-mobile-card-value">{point.lastTestDate}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Next Due</span>
                  <span className="rs-mobile-card-value">{point.nextTestDate}</span>
                </div>
              </div>
              <div className="rs-mobile-card-actions">
                <button className="rs-btn rs-btn-secondary" onClick={() => handleViewTestData(point)}>
                  <FontAwesomeIcon icon={faEye} /> View Report
                </button>
                <button className="rs-btn rs-btn-secondary" onClick={() => setDetailAsset(point)}>
                  <FontAwesomeIcon icon={faClockRotateLeft} /> Test History
                </button>
                {isSuperAdmin() && (<>
                <button className="rs-btn rs-btn-secondary" onClick={() => { setEditingAnchorPoint(point); setShowEditModal(true); }}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button className="rs-btn rs-btn-danger" onClick={() => handleDeleteAnchorPoint(point.id)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
                </>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      <CreateAnchorPointModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        propertyId={propertyId}
      />

      {showEditModal && editingAnchorPoint && (
        <EditAnchorPointModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          anchorPoint={editingAnchorPoint}
          onSuccess={updatedPoint => {
            setAnchorPoints(prev => prev.map(ap => ap.id === updatedPoint.id ? updatedPoint : ap));
            setShowEditModal(false);
          }}
        />
      )}

      <AssetDetailModal
        isOpen={!!detailAsset}
        onClose={() => setDetailAsset(null)}
        asset={detailAsset}
        assetType="anchor_point"
        propertyId={propertyId}
        assetMeta={detailAsset ? [
          { label: 'Type', value: detailAsset.type },
          { label: 'Installed', value: detailAsset.installationDate },
          { label: 'Reference', value: detailAsset.reference_id }
        ] : []}
      />
    </div>
  );
}
